import { Component, OnInit, OnDestroy, AfterViewInit, ViewChildren, QueryList, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Perfil, RespuestaPeticion, SSO, ModalButton } from '../../core/models';
import { AlertService, PerfilService, VinculacionDispositivosService, LoaderService, AppConfigService } from '../../core/services';
import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectorRef } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { parseISO, format } from 'date-fns';

/**
 * Componente que muestra la información de soporte.
 *
 * @author lreverendo
 * @author aarodriguezo
 *
 * @version 01.02.0400
 * @since 01.02.0100
 */
@Component({
  selector: 'app-dispositivos',
  templateUrl: 'dispositivos.component.html',
  styleUrls: ['./dispositivos.component.css']
})
export class DispositivosComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChildren('popOverHomeDelivery') public popOvers: QueryList<NgbPopover>;

  public tieneVinculosDispositivos = [];
  public dispositivosAnhadidos = [];

  public condicion = false;
  public listaDispositivosTodos = Object.values(AppConfigService.listaVinculacionDispositivos);

  private errorSubscription: Subscription;
  private perfilPacienteSubscription: Subscription;
  private paramsSubscription: Subscription;

  private ssoVinculacion: SSO;
  private perfilPaciente: Perfil;

  private clickInfoPopover = false;

  constructor(
    private alertService: AlertService,
    public loader: LoaderService,
    private perfilService: PerfilService,
    private translateService: TranslateService,
    private vinculacionDispositivosService: VinculacionDispositivosService,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.errorSubscription = this.perfilService.error$.subscribe(this.gestionarError.bind(this));
    this.perfilPacienteSubscription = this.perfilService.perfilPaciente$.subscribe(this.actualizarPerfilPaciente.bind(this));
    this.ssoVinculacion = this.vinculacionDispositivosService.getSSOVinculacion();

    if (this.ssoVinculacion != null) {
      this.vincularDispositivo();
    }
  }
  ngAfterViewInit() {
    if (!this.popOvers || this.popOvers.length === 0) {
      console.log('No popOverHomeDelivery elements found');
    }
  }
  public ngOnDestroy(): void {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }

    if (this.perfilPacienteSubscription) {
      this.perfilPacienteSubscription.unsubscribe();
    }

    if (this.paramsSubscription) {
      this.paramsSubscription.unsubscribe();
    }
  }
  public actualizarVinculosDispositivos(): void {
    // Reiniciar la lista tieneVinculosDispositivos
    this.tieneVinculosDispositivos = [];

    // Iterar sobre cada dispositivo en listaDispositivosTodos
    for (const dispositivo of this.listaDispositivosTodos) {
      const idSistema = dispositivo['nombre'];
      let existe = false;
      let fechaVinculacion = null;

      // Verificar que vinculosDispositivoPersona exista antes de llamar a some()
      if (this.perfilPaciente && Array.isArray(this.perfilPaciente.vinculosDispositivoPersona)) {
        existe = this.perfilPaciente.vinculosDispositivoPersona.some((vinculo) => dispositivo['idSistema'].includes(vinculo.idSistema));
        fechaVinculacion = this.perfilPaciente.vinculosDispositivoPersona.find((vinculo) => dispositivo['idSistema'].includes(vinculo.idSistema));
      }

      // Guardar el resultado en tieneVinculosDispositivos
      this.tieneVinculosDispositivos.push({
        idSistema,
        vinculado: existe,
        fechaVinculacion: fechaVinculacion ? fechaVinculacion.fechaVinculacion : null,
      });
    }
  }

  private actualizarDispositivosAnhadidos(): void {
    // Reiniciar la lista tieneVinculosDispositivos
    this.dispositivosAnhadidos = [];

    // Iterar sobre cada dispositivo en listaDispositivosTodos
    for (const dispositivo of this.listaDispositivosTodos) {
      const idSistema = dispositivo['nombre'];
      let entregaDomicilio = false;

      // Verificar que dispositivosPacienteAnhadidos exista antes de llamar a some()
      if (this.perfilPaciente && Array.isArray(this.perfilPaciente.dispositivosPacienteAnhadidos)) {
        entregaDomicilio = this.perfilPaciente.dispositivosPacienteAnhadidos.some((dispositivoPaciente) =>
          dispositivo['idSistema'].includes(dispositivoPaciente.modeloDispositivo.fabricanteDispositivo.fabricante.toUpperCase()) &&
          dispositivoPaciente.entregaDomicilio === true
        );
      }

      // Guardar el resultado en dispositivosAnhadidos
      this.dispositivosAnhadidos.push({
        idSistema,
        entregaDomicilio
        // tipoDispositivo
      });
    }
}


  public actualizarVinculosPerfil(): void {
    if (!this.perfilPaciente || !this.perfilPaciente.vinculosDispositivoPersona) {
      return;
    }

    // Obtener los ID de sistema de ambas listas
    const idsEnPerfil = new Set(this.perfilPaciente.vinculosDispositivoPersona.map((v) => v.idSistema));
    const idsEnTieneVinculos = new Set(this.tieneVinculosDispositivos.filter((v) => v.vinculado).map((v) => v.idSistema));

    // Añadir los que están en tieneVinculosDispositivos pero no en perfilPaciente
    this.tieneVinculosDispositivos.forEach((vinculo) => {
      if (vinculo.vinculado && !idsEnPerfil.has(vinculo.idSistema)) {
        this.perfilPaciente.vinculosDispositivoPersona.push({ idSistema: vinculo.idSistema, fechaVinculacion: vinculo.fechaVinculacion });
      }
    });

    // Eliminar los que están en perfilPaciente pero no en tieneVinculosDispositivos
    this.perfilPaciente.vinculosDispositivoPersona = this.perfilPaciente.vinculosDispositivoPersona.filter((v) =>
      idsEnTieneVinculos.has(v.idSistema)
    );

    this.cdr.detectChanges(); // Forzar la detección de cambios
  }

  public obtenerEstadoVinculo(idSistema: string): boolean {
    // Buscar el idSistema en tieneVinculosDispositivos y devolver el valor vinculado
    const dispositivo = this.tieneVinculosDispositivos.find((item) => item.idSistema === idSistema);
    return dispositivo ? dispositivo.vinculado : false;
  }

  public cambiarEstadoVinculo(idSistema: string, nuevoEstado: boolean): void {
    const dispositivo = this.tieneVinculosDispositivos.find((item) => item.idSistema === idSistema);

    if (dispositivo) {
      dispositivo.vinculado = nuevoEstado;
      this.cdr.detectChanges(); // Forzar la detección de cambios
    }
  }

  public irURLVinculacionDispositivo(nombreSistema: string): void {
    this.vinculacionDispositivosService.recuperarUrlVinculacionDispositivo(nombreSistema).subscribe(
      (urlVinculacion) => {
        window.location.href = urlVinculacion;
      },
      (_error) => {
        this.alertService.showAlertToast(this.translateService.instant('DISPOSITIVOS.VINCULACION.ERROR'));
      }
    );
  }

  public irURLDesvincularDispositivo(nombreSistema: string): void {
    this.vinculacionDispositivosService.setSSOVinculacion(null, null, nombreSistema);

    this.vinculacionDispositivosService.desvincularDispositivo({}).subscribe(
      (resp: RespuestaPeticion) => this.desvincularDispositivoExito(nombreSistema, resp),
      (error) => this.gestionarError(error)
    );
  }

  public vincularDispositivo() {
    if (this.ssoVinculacion.error != null) {
      this.alertService.showAlertToast(this.translateService.instant('DISPOSITIVOS.VINCULACION.ERROR'));
      this.vinculacionDispositivosService.setSSOVinculacion(null, null, null);
      this.ssoVinculacion = this.vinculacionDispositivosService.getSSOVinculacion();
    } else if (this.ssoVinculacion.bind_result != null) {
      const bind_result = this.ssoVinculacion.bind_result;
      if (bind_result === 'unbind') {
        this.alertService.showAlertToast(this.translateService.instant('DISPOSITIVOS.VINCULACION.UNBIND'));
        this.vinculacionDispositivosService.setSSOVinculacion(null, null, null);
        this.ssoVinculacion = this.vinculacionDispositivosService.getSSOVinculacion();
      } else if (bind_result === 'success' || bind_result === 'unchanged') {
        this.vinculacionDispositivosService.vincularDispositivoSuccess({}).subscribe(this.vincularDispositivoExito.bind(this), (error) => {
          this.gestionarError(error);
        });
      } else if (bind_result === 'user_already_bound_to_another_patient') {
        this.alertService.showAlertToast(this.translateService.instant('DISPOSITIVOS.VINCULACION.USER_ALREADY_BOUND'));
        this.vinculacionDispositivosService.setSSOVinculacion(null, null, null);
        this.ssoVinculacion = this.vinculacionDispositivosService.getSSOVinculacion();
      } else if (bind_result === 'patient_already_bound_to_another_user') {
        this.alertService.showAlertToast(this.translateService.instant('DISPOSITIVOS.VINCULACION.PATIENT_ALREADY_BOUND'));
        this.vinculacionDispositivosService.setSSOVinculacion(null, null, null);
        this.ssoVinculacion = this.vinculacionDispositivosService.getSSOVinculacion();
      }
    }
  }

  private actualizarPerfilPaciente(perfilPaciente: Perfil): void {
    if (perfilPaciente != null) {
      this.perfilPaciente = perfilPaciente;
      this.actualizarVinculosDispositivos();
      this.actualizarDispositivosAnhadidos();
      if (this.ssoVinculacion != null) {
        this.cambiarEstadoVinculo(
          this.ssoVinculacion.nombreSistema,
          this.perfilService.tieneVinculoDispositivo(this.ssoVinculacion.nombreSistema)
        );
      }
    }
  }

  private vincularDispositivoExito(resp: RespuestaPeticion): void {
    if (resp && resp.executionResult) {
      const dispositivo = this.tieneVinculosDispositivos.find((item) => item.idSistema === this.ssoVinculacion.nombreSistema);
      if (dispositivo) {
        dispositivo.fechaVinculacion = format(new Date() , "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
      }
      this.cambiarEstadoVinculo(this.ssoVinculacion.nombreSistema, true);    
      this.actualizarVinculosPerfil();
      

      this.perfilService.actualizarPerfilPaciente(this.perfilPaciente);

      
      this.alertService.lanzarExito(this.translateService.instant('DISPOSITIVOS.VINCULACION.SUCCESS'));
    } else {
      this.alertService.showAlertToast(this.translateService.instant('DISPOSITIVOS.VINCULACION.ERROR'));
    }
  }

  private desvincularDispositivoExito(nombreSistema: string, resp: RespuestaPeticion): void {
    if (resp && resp.executionResult) {
      this.cambiarEstadoVinculo(nombreSistema, false);
      this.actualizarVinculosPerfil();
      this.perfilService.actualizarPerfilPaciente(this.perfilPaciente);
      this.alertService.lanzarExito(this.translateService.instant('DISPOSITIVOS.DESVINCULACION.SUCCESS'));
    }
  }

  private gestionarError(error: HttpErrorResponse): void {
    if (error.status !== 401 && error.status !== 403) {
      this.alertService.lanzarError(error.status);
    }
  }

  public confirmarDesvincular(nombreSistema: string) {
    let buttons: ModalButton[] = [];

    buttons = [
      {
        texto: this.translateService.instant('DISPOSITIVOS.DESVINCULACION.DESVINCULAR'),
        type: 'button',
        accion: 'irURLDesvincularDispositivo',
        params: nombreSistema,
        cerrar: true
      }
    ];

    const data = {
      origen: this,
      titulo: this.translateService.instant('DISPOSITIVOS.DESVINCULACION.PREGUNTA_DESVINCULACION_SEGURO'),
      mensaje: this.translateService.instant('DISPOSITIVOS.DESVINCULACION.MENSAJE_DESVINCULACION_SEGURO') + nombreSistema,
      buttons: buttons,
      cerrar: true
    };

    this.alertService.modal.emit(data);
  }

  public avisoParaVincular(nombreSistema: string): void {
    let buttons: ModalButton[] = [];

    buttons = [
      {
        texto: this.translateService.instant('DISPOSITIVOS.VINCULACION.VINCULAR'),
        type: 'button',
        accion: 'irURLVinculacionDispositivo',
        params: nombreSistema,
        cerrar: true
      }
    ];

    const data = {
      origen: this,
      titulo: this.translateService.instant('DISPOSITIVOS.VINCULACION.TITULO_AVISO_TIEMPO_VINCULACION'),
      mensaje: this.translateService.instant('DISPOSITIVOS.VINCULACION.AVISO_TIEMPO_VINCULACION'),
      buttons: buttons,
      cerrar: true
    };

    this.alertService.modal.emit(data);
  }

  public estaVinculado(dispositivo: any): boolean {
    const vinculo = this.tieneVinculosDispositivos.find((v) => v.idSistema === dispositivo.nombre);
    return vinculo ? vinculo.vinculado : false;
  }

  public tieneHomeDelivery(dispositivo: any): boolean {
    const dispositivoPaciente = this.dispositivosAnhadidos.find((d) => d.idSistema === dispositivo.nombre);
    return dispositivoPaciente ? dispositivoPaciente.entregaDomicilio : false;
  }



  eventoClickInfoPopover(popOver: NgbPopover) {
    if (!this.clickInfoPopover) {

      if (popOver.isOpen()) {
      popOver.close();
    } else {
      popOver.open();
    }
     
    }}

  public abrirInfoPopover(popOver: NgbPopover): void {
    if (!this.clickInfoPopover) {
      popOver.open();
    }
  }


  @HostListener('document:click', ['$event'])
  public clickOutside(event): void {
    this.popOvers.forEach(popOver => {
      if (
        this.clickInfoPopover &&
        !event.target.classList.contains('icon-info_leyenda_2') &&
        !event.target.parentNode.classList.contains('migas-info')
      ) {
        popOver.close();
        this.clickInfoPopover = !this.clickInfoPopover;
      }
    });
  }


  public tieneBotonVinculacion(dispositivo: any): boolean {
    const dispositivoPaciente = this.listaDispositivosTodos.find((d) => d['nombre'] === dispositivo.nombre);
    return dispositivoPaciente['botonVinculacion'];
  }

public mostrarFecha(dispositivo: any) : string {
  const dispositivoPaciente = this.tieneVinculosDispositivos.find((d) => d.idSistema === dispositivo.nombre);
  if (dispositivoPaciente && dispositivoPaciente.fechaVinculacion) {

    const fecha = parseISO(dispositivoPaciente.fechaVinculacion);
    const fechaFormateada = format(fecha, 'dd/MM/yyyy');
    return fechaFormateada;
  }
  return '';
}

}